import {
	ASSIGNMENT_DETAILS_ROUTE_MESSAGES,
	ASSIGNMENT_EDIT_ROUTE_MESSAGES,
	CAREER_BEFORE_ASSIGNMENT_START_MESSAGES,
	CAREER_ROUTE_MESSAGES,
	CLIENT_EDIT_FIELDS,
	CLIENT_EDIT_ROUTE_MESSAGES,
	DISABILITIES_ROUTE_MESSAGES,
	DISABILITY_PROOF_ADD_MESSAGES,
	DISABILITY_PROOF_UPLOAD_MESSAGES,
	DISABILITY_TYPE_ADD_MESSAGES,
	END_REPORT_EDITOR_MESSAGES,
	INSTITUTION_BRANCH_ADDRESS_MESSAGES,
	START_REPORT_EDITOR_MESSAGES,
	isEndReportSachstandMessage,
	normalizeValidationMessage,
} from './assignment-validation-route-messages';
import { DB_PHASES } from '../../api/app-types/phase.app';

/**
 * Splits a URL into its path and query parameters.
 * @param url The URL to split into path and query parameters.
 * @description This function takes a URL string, splits it into the path and query parameters,
 * and returns an object containing the path and a record of query parameters.
 * The query parameters are decoded from their URL-encoded format.
 * @returns An object containing the path and a record of query parameters.
 */
export const splitPathAndQueryParameters = (
	url: string,
): { path: string; queryParams: Record<string, string>; segments: string[] } => {
	const [path, queryString] = url.split('?');
	const queryParams: Record<string, string> = {};

	if (queryString) {
		queryString.split('&').forEach((param) => {
			const [key, value] = param.split('=');
			if (key && value) {
				queryParams[key] = decodeURIComponent(value);
			}
		});
	}

	// split path into url segments
	const segments = path.split('/').filter((segment) => segment.length > 0);
	if (segments.length === 0) {
		return { path: '/', queryParams, segments };
	}
	if (segments.length === 1 && segments[0] === '') {
		return { path: '/', queryParams, segments };
	}

	return { path, queryParams, segments };
};

/**
 * Depending on the property name of a validation error on the assignment move page,
 * maps the error to a route so the user can check it on the correct page.
 *
 * @param propertyName the property name of the entity with the validation error
 * @param assignmentId the ID of the assignment
 * @param clientId the ID of the client
 * @param institutionId the ID of the institution (optional)
 * @param reportId the ID of the report (optional)
 * @param isServiceOfficeStatement whether the assignment is a service office statement (optional)
 * @param isAdditionalAssignment whether the assignment is an additional assignment (optional)
 * @param subPropertyNames the sub property names of the entity with the validation error (optional)
 * @param message the error message (optional)
 * @param disabilityReportId the ID of the disability report (optional)
 * @param careerId the ID of the career entry (optional)
 * @param isMainInstitution whether the institution is the main institution or a branch (optional)
 * @param mainInstitutionId the ID of the main institution (optional)
 * @param targetPhase the phase the assignment is currently transitioning to (optional)
 * @returns the route segments and optionally a toast message key
 */
export function mapAssignmentErrorPropertyToRouteSegments(
	propertyName: string,
	assignmentId: number | string,
	clientId: number | string,
	institutionId?: number | string,
	reportId?: number | string,
	isServiceOfficeStatement?: boolean,
	isAdditionalAssignment?: boolean,
	subPropertyNames?: string[],
	message?: string,
	disabilityReportId?: number | string,
	careerId?: number | string,
	isMainInstitution?: boolean,
	mainInstitutionId?: number | string,
	targetPhase?: number | string,
) {
	const normalizedPropertyName = propertyName?.trim().toLowerCase();
	const normalizedMessage = normalizeValidationMessage(message);
	const normalizedSubPropertyNames = (subPropertyNames ?? []).map((sp) => (sp ?? '').toLowerCase());

	const editAssignmentRoute = isServiceOfficeStatement
		? ['/desk', 'client', clientId, 'assignments', assignmentId, 'edit-fds']
		: isAdditionalAssignment
			? ['/desk', 'client', clientId, 'assignments', assignmentId, 'edit-additional']
			: ['/desk', 'client', clientId, 'assignments', assignmentId, 'edit'];
	const assignmentDetailsRoute = isServiceOfficeStatement
		? ['/desk', 'client', clientId, 'assignments', assignmentId, 'fds']
		: isAdditionalAssignment
			? ['/desk', 'client', clientId, 'assignments', assignmentId, 'additional']
			: ['/desk', 'client', clientId, 'assignments', assignmentId];
	const reportRoute = isServiceOfficeStatement
		? reportId
			? ['/desk', 'client', clientId, 'assignments', assignmentId, 'fds', 'template', reportId]
			: ['/desk', 'client', clientId, 'assignments', assignmentId, 'fds', 'template']
		: assignmentDetailsRoute;
	const disabilitiesRoute = ['/desk', 'client', clientId, 'disabilities'];
	const disabilityTypeAddRoute = [...disabilitiesRoute, 'type', 'add'];
	const disabilityProofCreateRoute = [...disabilitiesRoute, 'proofs', 'create'];
	const disabilityProofEditRoute = [...disabilitiesRoute, 'proofs', disabilityReportId, 'edit'];
	const clientEditRoute = ['/desk', 'client', clientId, 'edit'];
	const startReportRoute = reportId
		? ['/desk', 'client', clientId, 'assignments', assignmentId, 'report', reportId]
		: assignmentDetailsRoute;
	const endReportRoute = reportId
		? ['/desk', 'client', clientId, 'assignments', assignmentId, 'end-report', reportId]
		: assignmentDetailsRoute;
	const careerOverviewRoute = ['/desk', 'client', clientId, 'career'];
	const currentCareerRoute = careerId ? ['/desk', 'client', clientId, 'career', careerId] : careerOverviewRoute;
	const institutionRoute = isMainInstitution
		? ['/desk', 'institution', mainInstitutionId, 'edit']
		: ['/desk', 'institution', mainInstitutionId, 'workplace', institutionId, 'edit'];
	const isTransitionToPhase3 = String(targetPhase) === String(DB_PHASES.P3);

	// PGP4_01_21_01 .. _09 and text-identical PGP3 messages are matched by core phrases.
	// Without a target-phase hint, this keeps the previous fallback to end report.
	if (!isServiceOfficeStatement && isEndReportSachstandMessage(normalizedMessage)) {
		return { route: isTransitionToPhase3 ? startReportRoute : endReportRoute };
	}

	// Resolve unambiguous message sets independently of the reported BE area/property.
	if (ASSIGNMENT_EDIT_ROUTE_MESSAGES.has(normalizedMessage)) {
		return { route: editAssignmentRoute };
	}
	if (ASSIGNMENT_DETAILS_ROUTE_MESSAGES.has(normalizedMessage)) {
		return { route: assignmentDetailsRoute };
	}
	if (CLIENT_EDIT_ROUTE_MESSAGES.has(normalizedMessage)) {
		return { route: clientEditRoute };
	}
	if (DISABILITY_TYPE_ADD_MESSAGES.has(normalizedMessage)) {
		return { route: disabilityTypeAddRoute };
	}
	if (DISABILITY_PROOF_UPLOAD_MESSAGES.has(normalizedMessage)) {
		return { route: disabilityProofEditRoute };
	}
	if (DISABILITY_PROOF_ADD_MESSAGES.has(normalizedMessage)) {
		return { route: disabilityProofCreateRoute };
	}
	if (DISABILITIES_ROUTE_MESSAGES.has(normalizedMessage)) {
		return { route: disabilitiesRoute };
	}
	if (CAREER_BEFORE_ASSIGNMENT_START_MESSAGES.has(normalizedMessage)) {
		return { route: currentCareerRoute };
	}
	if (CAREER_ROUTE_MESSAGES.has(normalizedMessage)) {
		return { route: careerOverviewRoute };
	}
	if (INSTITUTION_BRANCH_ADDRESS_MESSAGES.has(normalizedMessage)) {
		return { route: institutionRoute };
	}
	if (START_REPORT_EDITOR_MESSAGES.has(normalizedMessage)) {
		return { route: startReportRoute };
	}
	if (END_REPORT_EDITOR_MESSAGES.has(normalizedMessage)) {
		return { route: endReportRoute };
	}

	switch (normalizedPropertyName) {
		case 'assignment': {
			if (CAREER_BEFORE_ASSIGNMENT_START_MESSAGES.has(normalizedMessage)) {
				return {
					route: currentCareerRoute,
				};
			}
			if (DISABILITY_PROOF_UPLOAD_MESSAGES.has(normalizedMessage)) {
				return { route: disabilityProofEditRoute };
			}
			if (ASSIGNMENT_DETAILS_ROUTE_MESSAGES.has(normalizedMessage)) {
				return { route: assignmentDetailsRoute };
			}
			if (DISABILITIES_ROUTE_MESSAGES.has(normalizedMessage)) {
				return { route: disabilitiesRoute };
			}
			if (START_REPORT_EDITOR_MESSAGES.has(normalizedMessage)) {
				return { route: startReportRoute };
			}
			if (END_REPORT_EDITOR_MESSAGES.has(normalizedMessage)) {
				return { route: endReportRoute };
			}

			return { route: editAssignmentRoute };
		}
		case 'client': {
			const goToClientEdit =
				normalizedSubPropertyNames.some((sp) => CLIENT_EDIT_FIELDS.has(sp)) ||
				CLIENT_EDIT_ROUTE_MESSAGES.has(normalizedMessage);

			return {
				route: goToClientEdit ? clientEditRoute : editAssignmentRoute,
			};
		}
		case 'address':
			return { route: clientEditRoute };

		case 'career': {
			const hasCareers = normalizedSubPropertyNames.includes('careers');

			return {
				route: hasCareers ? ['/desk', 'client', clientId, 'career', 'add'] : careerOverviewRoute,
			};
		}
		case 'disabilityreport': {
			if (DISABILITIES_ROUTE_MESSAGES.has(normalizedMessage)) {
				return { route: disabilitiesRoute };
			}
			if (DISABILITY_PROOF_UPLOAD_MESSAGES.has(normalizedMessage)) {
				return { route: disabilityProofEditRoute };
			}
			if (DISABILITY_TYPE_ADD_MESSAGES.has(normalizedMessage)) {
				return { route: disabilityTypeAddRoute };
			}

			return { route: disabilityProofCreateRoute };
		}
		case 'disabilitykindresponse': {
			if (CLIENT_EDIT_ROUTE_MESSAGES.has(normalizedMessage)) {
				return { route: clientEditRoute };
			}
			if (DISABILITIES_ROUTE_MESSAGES.has(normalizedMessage)) {
				return { route: disabilitiesRoute };
			}
			if (DISABILITY_PROOF_ADD_MESSAGES.has(normalizedMessage)) {
				return { route: disabilityProofCreateRoute };
			}

			return { route: disabilityTypeAddRoute };
		}
		case 'serviceofficestatement':
			return { route: editAssignmentRoute };

		case 'report': {
			if (ASSIGNMENT_EDIT_ROUTE_MESSAGES.has(normalizedMessage)) {
				return { route: editAssignmentRoute };
			}
			if (START_REPORT_EDITOR_MESSAGES.has(normalizedMessage)) {
				return { route: startReportRoute };
			}
			if (END_REPORT_EDITOR_MESSAGES.has(normalizedMessage)) {
				return { route: endReportRoute };
			}
			return { route: reportRoute };
		}
		case 'institutionbranch': {
			if (INSTITUTION_BRANCH_ADDRESS_MESSAGES.has(normalizedMessage)) {
				return { route: institutionRoute };
			}

			return { route: careerOverviewRoute };
		}
		case 'assignmentimpediment':
			if (isServiceOfficeStatement) return { route: editAssignmentRoute };

			return reportId
				? { route: startReportRoute }
				: {
						route: assignmentDetailsRoute,
						toastMessageKey: 'base.notification_message.initial_report_required',
					};

		case 'assistanceresult':
			return { route: assignmentDetailsRoute };

		case 'general': {
			if (END_REPORT_EDITOR_MESSAGES.has(normalizedMessage)) {
				return { route: endReportRoute };
			}

			const goToReports = normalizedSubPropertyNames.includes('assignmentimpediment');

			return { route: goToReports ? assignmentDetailsRoute : editAssignmentRoute };
		}

		case 'contact':
			return { route: ['/desk', 'client', clientId, 'assignments'] };

		case 'reportdocument':
			return { route: assignmentDetailsRoute };

		case 'categorytask':
			return { route: ['/desk', 'client', clientId, 'assignments'] };

		default:
			return { route: ['/unknown-error'] };
	}
}
